import React, { useState, useEffect } from 'react';
import { RefreshCw, CheckCircle2, XCircle, AlertTriangle, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { dataEngineApi } from '../../services/dataEngineApi';

const STATUS_BADGES = {
  syncing: { icon: Loader2, color: 'text-blue-400', bg: 'bg-blue-400/10', label: 'Syncing', animate: true },
  pass: { icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-400/10', label: 'Pass' },
  fail: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-400/10', label: 'Fail' },
  error: { icon: AlertTriangle, color: 'text-yellow-400', bg: 'bg-yellow-400/10', label: 'Error' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_BADGES[status] || STATUS_BADGES.error;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${cfg.bg} ${cfg.color}`}>
      <Icon className={`h-3 w-3 ${cfg.animate ? 'animate-spin' : ''}`} />
      {cfg.label}
    </span>
  );
}

function formatCurrency(val) {
  if (val == null) return '—';
  const abs = Math.abs(val);
  const formatted = abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return val < 0 ? `(${formatted})` : formatted;
}

function VarianceDetail({ variance }) {
  if (!variance || variance.length === 0) return null;
  return (
    <div className="px-3 py-2 bg-gray-900/60">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-gray-500">
            <th className="text-left py-1 font-medium w-1/4">Category</th>
            <th className="text-right py-1 font-medium w-1/4">QuickBooks</th>
            <th className="text-right py-1 font-medium w-1/4">Cube</th>
            <th className="text-right py-1 font-medium w-1/4">Variance</th>
          </tr>
        </thead>
        <tbody>
          {variance.map((v, i) => (
            <tr key={i} className={v.match ? '' : 'bg-red-500/5'}>
              <td className="py-1 text-gray-300">{v.category}</td>
              <td className="py-1 text-right text-gray-400 font-mono">{formatCurrency(v.qb)}</td>
              <td className="py-1 text-right text-gray-400 font-mono">{formatCurrency(v.cube ?? v.duckdb)}</td>
              <td className={`py-1 text-right font-mono ${v.match ? 'text-emerald-400/70' : 'text-red-400'}`}>
                {v.diff === 0 ? '—' : formatCurrency(v.diff)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function BulkReconcileTab({ companies = [], onSelectCompany }) {
  const [runs, setRuns] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [results, setResults] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => { loadRuns(); }, []);
  useEffect(() => { if (selectedRunId) loadResults(selectedRunId); }, [selectedRunId, statusFilter]);

  // Auto-refresh every 10s when run is in progress
  useEffect(() => {
    if (selectedRun?.status !== 'running') return;
    const interval = setInterval(() => { if (selectedRunId) loadResults(selectedRunId); }, 5000);
    return () => clearInterval(interval);
  }, [selectedRun?.status, selectedRunId]);

  const loadRuns = async () => {
    try {
      const data = await dataEngineApi.getSyncReconciliationRuns(10);
      setRuns(data || []);
      if (data?.length > 0 && !selectedRunId) {
        setSelectedRunId(data[0].id);
        setSelectedRun(data[0]);
      }
    } catch (err) {
      console.error('Failed to load runs:', err);
    }
  };

  const loadResults = async (runId) => {
    setLoading(true);
    try {
      const [run, res] = await Promise.all([
        dataEngineApi.getSyncReconciliationRun(runId),
        dataEngineApi.getSyncReconciliationResults(runId, statusFilter === 'all' ? null : statusFilter),
      ]);
      setSelectedRun(run);
      const enriched = (res || []).map(r => {
        const company = companies.find(c => c.rf_id === r.rf_id);
        return { ...r, company_name: r.company_name || company?.display_name || r.rf_id };
      });
      // Sort: syncing first, then by time descending
      const statusOrder = { syncing: 0, error: 1, fail: 2, pass: 3 };
      enriched.sort((a, b) => (statusOrder[a.status] ?? 9) - (statusOrder[b.status] ?? 9));
      setResults(enriched);
    } catch (err) {
      console.error('Failed to load results:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(prev => prev === id ? null : id);
  };

  const progressPct = selectedRun
    ? Math.round((selectedRun.completed_count / Math.max(selectedRun.total_companies, 1)) * 100)
    : 0;

  return (
    <div className="p-4 space-y-4">
      {/* Run selector */}
      <div className="flex items-center gap-3">
        <select
          value={selectedRunId || ''}
          onChange={(e) => { setSelectedRunId(e.target.value); setExpandedId(null); }}
          className="bg-gray-800 border border-gray-700 text-gray-200 text-xs rounded px-3 py-1.5 focus:ring-blue-500 focus:border-blue-500"
        >
          {runs.map(r => (
            <option key={r.id} value={r.id}>
              {new Date(r.started_at).toLocaleString()} — {r.status} ({r.passed_count}✓ {r.failed_count}✗ {r.errored_count}!)
            </option>
          ))}
        </select>
        <button onClick={loadRuns} className="text-gray-400 hover:text-gray-200 p-1">
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Summary cards */}
      {selectedRun && (
        <div className="space-y-3">
          {selectedRun.status === 'running' && (
            <div className="w-full bg-gray-800 rounded-full h-1.5">
              <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
            </div>
          )}
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-gray-800/50 rounded-lg px-3 py-2 border border-gray-700/50">
              <div className="text-[11px] text-gray-500">Total</div>
              <div className="text-lg font-semibold text-gray-200">{selectedRun.total_companies}</div>
            </div>
            <div className="bg-gray-800/50 rounded-lg px-3 py-2 border border-emerald-900/30">
              <div className="text-[11px] text-emerald-400/70">Passed</div>
              <div className="text-lg font-semibold text-emerald-400">{selectedRun.passed_count}</div>
            </div>
            <div className="bg-gray-800/50 rounded-lg px-3 py-2 border border-red-900/30">
              <div className="text-[11px] text-red-400/70">Failed</div>
              <div className="text-lg font-semibold text-red-400">{selectedRun.failed_count}</div>
            </div>
            <div className="bg-gray-800/50 rounded-lg px-3 py-2 border border-yellow-900/30">
              <div className="text-[11px] text-yellow-400/70">Errors</div>
              <div className="text-lg font-semibold text-yellow-400">{selectedRun.errored_count}</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-1.5">
        {['all', 'syncing', 'pass', 'fail', 'error'].map(s => (
          <button
            key={s}
            onClick={() => { setStatusFilter(s); setExpandedId(null); }}
            className={`px-2.5 py-1 text-xs rounded transition-colors ${
              statusFilter === s
                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                : 'text-gray-500 hover:text-gray-300 border border-transparent'
            }`}
          >
            {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {/* Results table */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-700/50">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-800/80 text-gray-400">
                <th className="w-6 px-2 py-2"></th>
                <th className="text-left px-3 py-2 font-medium">Company</th>
                <th className="text-left px-3 py-2 font-medium">Type</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-left px-3 py-2 font-medium">Details</th>
                <th className="text-left px-3 py-2 font-medium">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {results.map(r => {
                const totalVariance = (r.variance || []).reduce((sum, v) => sum + Math.abs(v.diff || 0), 0);
                const isExpanded = expandedId === r.id;
                const hasVariance = r.variance && r.variance.length > 0;
                return (
                  <React.Fragment key={r.id}>
                    <tr
                      className={`hover:bg-gray-800/30 cursor-pointer transition-colors ${isExpanded ? 'bg-gray-800/20' : ''}`}
                      onClick={() => hasVariance ? toggleExpand(r.id) : null}
                    >
                      <td className="px-2 py-2 text-gray-600">
                        {hasVariance ? (
                          isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />
                        ) : null}
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-gray-200">{r.company_name}</div>
                        <div className="text-[10px] text-gray-600 font-mono">{r.rf_id}</div>
                      </td>
                      <td className="px-3 py-2 text-gray-500">{r.report_type}</td>
                      <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                      <td className="px-3 py-2">
                        {r.status === 'syncing' ? (
                          <span className="text-blue-400/80 text-[11px]">Sync in progress…</span>
                        ) : r.status === 'error' ? (
                          <span className="text-red-400/80 text-[11px]" title={r.error_message}>
                            {(r.error_message || 'Unknown error').slice(0, 80)}
                          </span>
                        ) : r.status === 'pass' ? (
                          <span className="text-emerald-400/70">All categories match</span>
                        ) : (
                          <span className="text-red-300">${totalVariance.toLocaleString()} total variance</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-500">
                        {r.created_at ? new Date(r.created_at).toLocaleTimeString() : '—'}
                      </td>
                    </tr>
                    {isExpanded && hasVariance && (
                      <tr>
                        <td colSpan={6} className="p-0">
                          <VarianceDetail variance={r.variance} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {results.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-gray-600">No results</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
